import useAxios from '@hooks/useFetch';
import { components, paths } from '@api/api';
import { useCallback, useMemo } from 'react';
import { Sort } from "@models";

const TOP_TRENDS_MENU_EXAMPLES_ENDPOINT: keyof paths = '/TopTrendMenuExamples';
const TOP_TRENDS_MENU_EXAMPLES_EXPORT_ENDPOINT: keyof paths = '/TopTrendMenuExampleExport';

export type TopTrendsMenuExamplesRequest = components['schemas']['Application.TopTrends.Models.TopTrendMenuExamplesFilterInput'];
export type TopTrendsMenuExamplesRequestParameters = components['schemas']['Application.TopTrends.Models.TopTrendMenuExamplesFilterInput.TParameters'];
export type TopTrendsMenuExamplesResponse = components['schemas']['Application.Byo.Models.MenuExamplesOutputModel'];
export type MenuItemExample = components["schemas"]["Application.Byo.Models.MenuExamplesOutputModel.MenuItemExample"]
export type TopTrendsMenuExamplesExportRequest = components['schemas']['Infrastructure.DTOs.TopTrendMenuExampleExportFilter'];
export type FilterWord = components['schemas']['MenuTrends.Application.Contracts.Models.FilterWord'];
export type MainFilter = components['schemas']['Application.TopTrends.Models.BaseTopTrendMainFilterInput'];
export type Filter = components['schemas']['Application.Common.Models.MainFilterInput.Filter'];

type FetchOptions = {
    sortColumn: TopTrendsMenuExamplesRequestParameters['sortColumn'];
    search: string;
    take: number;
    skip: number;
    sortDir: string;
};

type FetchResponse = {
    data: MenuItemExample[];
    totalRecords: number;
};

export interface UseTopTrendsMenuExamplesParams {
    word: FilterWord | null;
    mainFilter: MainFilter;
    fullFilter?: Filter;
}

export default function useTopTrendsMenuExamples({
    word,
    mainFilter,
    fullFilter,
}: UseTopTrendsMenuExamplesParams) {
    const {
        doFetch,
        cancel: cancelFetch,
    } = useAxios<TopTrendsMenuExamplesResponse, TopTrendsMenuExamplesRequest>({
        method: 'post',
        url: TOP_TRENDS_MENU_EXAMPLES_ENDPOINT,
        lazy: true,
    });

    const {
        doFetch: doExport,
        cancel: cancelExport,
    } = useAxios<string, TopTrendsMenuExamplesExportRequest>({
        method: 'post',
        url: TOP_TRENDS_MENU_EXAMPLES_EXPORT_ENDPOINT,
        lazy: true,
    });

    const fetch = useCallback(
        async (options: FetchOptions): Promise<FetchResponse> => {
            if (!word) {
                return { data: [], totalRecords: 0 };
            }

            const requestData: TopTrendsMenuExamplesRequest = {
                mainFilter: {
                    ...mainFilter,
                    word,
                },
                tableParameters: {
                    sortColumn: options.sortColumn,
                    search: options.search,
                    take: options.take,
                    skip: options.skip,
                    sortDir: options.sortDir === Sort.Asc ? 0 : 1,
                },
            };

            const response = await doFetch({ data: requestData });

            return {
                data: response?.data?.data ?? [],
                totalRecords: response?.data?.totalRecords ?? 0,
            };
        },
        [word, mainFilter, doFetch]
    );

    const fetchExport = useCallback(
        async (options: FetchOptions): Promise<string | void> => {
            if (!word) {
                return;
            }

            const requestData: TopTrendsMenuExamplesExportRequest = {
                fullFilter,
                customerFilter: {
                    mainFilter: {
                        ...mainFilter,
                        word,
                    },
                    tableParameters: {
                        sortColumn: options.sortColumn,
                        search: options.search,
                        take: options.take,
                        skip: options.skip,
                        sortDir: options.sortDir === Sort.Asc ? 0 : 1,
                    },
                },
            };

            const response = await doExport({ data: requestData });
            return response?.data;
        },
        [word, mainFilter, fullFilter, doExport]
    );

    const cancel = useCallback(() => {
        cancelFetch();
        cancelExport();
    }, [cancelFetch, cancelExport]);

    const isReady = useMemo(() => !!word, [word]);

    return {
        fetch,
        fetchExport,
        cancel,
        isReady,
        downloadUrl: TOP_TRENDS_MENU_EXAMPLES_EXPORT_ENDPOINT,
    };
}
